import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormFieldContext from 'components/forms/FormFieldContext';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { fetchData } from 'services/api/fetchData';
import { formatDate } from 'services/utils/dateTimeUtils';
import JobModal from './JobModal';
import '../../style/scss/modals.scss';

/*
 * JobCreationDetailsPanel.tsx
 * Shows extra details of the selected client as a side tab next to the
 * job creation form. It shows the pinned note of the client and the three
 * most recent jobs of that client, which can be opened by clicking on them.
 */

interface PinnedNoteType {
    id: number;
    note: string;
    created: string;
    created_by_user_name: string | null;
}

interface RecentJobType {
    id: number;
    number: string | null;
    type_name: string | null;
    main_description_plain: string;
    status_name: string | null;
    status_condition: string | null;
    first_appointment_start: string | null;
    last_appointment_end: string | null;
    target_date: string | null;
}

const JobCreationDetailsPanel: React.FC<{
    visibleWhen?: (updatedData: Record<string, any>) => boolean;
}> = ({
    visibleWhen,
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { handleLogout } = useAuthContext();
    const { initializeModal } = useModal();
    const { updatedData } = useContext(FormFieldContext);
    const [pinnedNote, setPinnedNote] = useState<PinnedNoteType | null>(null);
    const [recentJobs, setRecentJobs] = useState<RecentJobType[]>([]);
    const [loading, setLoading] = useState(false);

    // Get the id of the currently selected client
    const clientId = updatedData?.client?.id ?? null;

    // Determine if the panel may be shown within the current form context
    const isVisible = clientId && (!visibleWhen || visibleWhen(updatedData ?? {}));

    // Fetch the details of the selected client
    useEffect(() => {
        // Empty the details if no client is selected
        if (!isVisible) {
            setPinnedNote(null);
            setRecentJobs([]);
            return;
        }

        // Fetch the pinned note and the recent jobs of the client
        const fetchClientDetails = async () => {
            setLoading(true);

            try {
                const response = await fetchData({
                    apiUrl: `get_job_creation_details/${clientId}`,
                    handleLogout,
                });

                setPinnedNote(response?.pinned_note ?? null);
                setRecentJobs(response?.recent_jobs ?? []);
            } finally {
                setLoading(false);
            }
        };

        fetchClientDetails();
    }, [clientId, isVisible]);

    // Open the job modal of the clicked job
    const handleJobClick = (jobId: number) => {
        initializeModal(
            React.cloneElement(<JobModal />, { itemId: jobId }),
            { itemId: jobId, modalSize: 'large' }
        );
    };

    // Translate the default status names, the same way the job list does
    const formatStatusName = (statusName: string | null) => {
        if (!statusName) return '';
        return t(`workflow.default_status_names.${statusName}`, { defaultValue: statusName });
    };

    // Format the date of a job as a single date or as a from to date range
    const formatJobDate = (job: RecentJobType) => {
        // Show the target date if the job has no appointments yet
        if (!job.first_appointment_start) {
            return job.target_date ? formatDate(job.target_date, userLocale) : '';
        }

        // Format the start and end date of the appointment range
        const startDate = formatDate(job.first_appointment_start, userLocale);
        const endDate = job.last_appointment_end ? formatDate(job.last_appointment_end, userLocale) : null;

        // Show a single date if the range starts and ends on the same day
        if (!endDate || startDate === endDate) return startDate;

        return `${startDate} - ${endDate}`;
    };

    // Don't render the side tab if no client is selected
    if (!isVisible) return null;

    return (
        <aside className='form-modal-sidetab'>

            {/* Show the pinned note of the client */}
            {pinnedNote && (
                <div className='sidetab-section'>
                    <div className='sidetab-header'>{t('job.creation_details.pinned_note_header')}</div>
                    <div className='pinned-note'>
                        <div className='pinned-note-content'
                             dangerouslySetInnerHTML={{ __html: pinnedNote.note }} />
                        {pinnedNote.created_by_user_name && (
                            <div className='pinned-note-meta'>
                                {pinnedNote.created_by_user_name}, {formatDate(pinnedNote.created, userLocale)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Show the most recent jobs of the client */}
            <div className='sidetab-section'>
                <div className='sidetab-header'>{t('job.creation_details.recent_jobs_header')}</div>

                {recentJobs.length > 0 ? (
                    recentJobs.map((job) => (
                        <div key={job.id} className='recent-job' onClick={() => handleJobClick(job.id)}>
                            <div className='recent-job-line'>
                                <span className='recent-job-type'>{job.type_name || job.number}</span>
                                <span className='recent-job-status'>{formatStatusName(job.status_name)}</span>
                            </div>
                            <div className='recent-job-line'>
                                <span className='recent-job-description'>{job.main_description_plain}</span>
                                <span className='recent-job-date'>{formatJobDate(job)}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    !loading && <div className='sidetab-empty'>{t('job.creation_details.no_recent_jobs')}</div>
                )}
            </div>
        </aside>
    );
};

export default JobCreationDetailsPanel;
